import { type ReactNode, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/client/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import { ApiError } from "@/client/lib/api";

/**
 * One confirmation dialog for the whole app (PE-934) so every destructive action
 * looks and behaves the same: a trigger, a title/description, optional body, and
 * a Cancel + confirm pair. `onConfirm` runs async; the dialog closes on success
 * and, on failure, stays open with an error toast (so the user can retry) - it
 * never dismisses silently. Buttons are disabled while the action is in flight.
 */
export function ConfirmDialog({
  trigger,
  title,
  description,
  confirmLabel = "Confirm",
  pendingLabel = "Working…",
  cancelLabel = "Cancel",
  onConfirm,
  confirmDisabled = false,
  variant = "destructive",
  errorMessage = "Something went wrong. Please try again.",
  onOpenChange,
  children,
}: {
  trigger: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  pendingLabel?: string;
  cancelLabel?: string;
  onConfirm: () => Promise<void>;
  confirmDisabled?: boolean;
  variant?: "destructive" | "default";
  errorMessage?: string;
  /** Notified on open/close so callers can reset transient body state. */
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);

  const setOpenState = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const handleConfirm = async () => {
    setWorking(true);
    try {
      await onConfirm();
      setOpenState(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : errorMessage);
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpenState}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {children}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={working}>
              {cancelLabel}
            </Button>
          </DialogClose>
          <Button variant={variant} onClick={handleConfirm} disabled={working || confirmDisabled}>
            {working ? pendingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
